import { Badge, Box, Button, Group, Stack, Text, Title } from "@mantine/core";
import { OnboardingStage } from "./OnboardingStage";

const CONTROL_HEIGHT = 64;

function CircleCheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9.25" stroke="var(--mantine-color-teal-6)" strokeWidth="1.5" />
      <path
        d="M8.5 12.25 11 14.75l4.5-5"
        stroke="var(--mantine-color-teal-6)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m6 4 4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArtworkIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="7.25" cy="8" r="1.25" fill="currentColor" />
      <path
        d="m3.5 14 3.75-3.5 2.75 2.5 2.5-2.25L16.5 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 2.75c2 2 2 12.5 0 14.5M10 2.75c-2 2-2 12.5 0 14.5M3 10h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ContactIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="8" cy="7" r="2.75" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.75 16.25c0-2.9 2.35-5 5.25-5s5.25 2.1 5.25 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M15.75 6.5v4M17.75 8.5h-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CompassIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m13 7-1.6 4.4L7 13l1.6-4.4L13 7Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface GetStartedAction {
  key: string;
  label: string;
  /** Destination under the Pro app's hash router, relative to its `#/` base. */
  path: string;
  recommended?: boolean;
  Icon: () => JSX.Element;
}

/** A GetStartedAction with its environment-resolved destination URL. */
export type GetStartedChoice = GetStartedAction & { href: string };

// The Pro base URL is environment-dependent (localhost in dev, pro-qa on
// staging) and injected by the server via Artcloud.url.init; production is
// only a fallback for contexts where that bootstrap didn't run.
const PRO_FALLBACK_BASE_URL = "https://pro.artcloud.com/#/";

// Exported for the gallery v2 screen (ACA-266 GetStartedStep.tsx), which imports this rather than
// re-deriving Pro's base URL - a destination can only have one source. Behaviour is unchanged.
export function resolveProUrl(path: string): string {
  const base =
    (typeof window !== "undefined" &&
      (
        window as unknown as {
          Artcloud?: { url?: { pro?: { baseUrl?: string } } };
        }
      ).Artcloud?.url?.pro?.baseUrl) ||
    PRO_FALLBACK_BASE_URL;
  return base + path;
}

// Fixed order, no intent-based reordering (ACA-37). Destinations are the Pro
// hash-routed app; "explore" lands on the product home and carries no
// personalisation implication.
export const GET_STARTED_ACTIONS: GetStartedAction[] = [
  {
    key: "artwork",
    label: "Add your first artwork",
    path: "inventory",
    recommended: true,
    Icon: ArtworkIcon,
  },
  {
    key: "theme",
    label: "Set up your site theme",
    path: "website/design?selectTheme=1",
    Icon: GlobeIcon,
  },
  {
    key: "contacts",
    label: "Add your contact(s)",
    path: "clients",
    Icon: ContactIcon,
  },
  {
    key: "explore",
    label: "Start Exploring",
    path: "",
    Icon: CompassIcon,
  },
];

export interface GetStartedScreenProps {
  firstName?: string;
  signedInAs?: string;
  /** Optional hook fired before the row's native navigation (e.g. analytics). */
  onChoose?: (action: GetStartedChoice) => void;
}

export function GetStartedScreen({
  firstName,
  signedInAs,
  onChoose,
}: GetStartedScreenProps) {
  const trimmedName = firstName?.trim();
  const heading = trimmedName
    ? `You're all set, ${trimmedName}.`
    : "You're all set.";

  return (
    <OnboardingStage activeStep={3} signedInAs={signedInAs}>
      <Stack gap="lg">
        <Box>
          <Group gap="sm" wrap="nowrap" align="center">
            <CircleCheckIcon />
            <Title
              order={1}
              fz="30px"
              fw={500}
              lh={1.2}
              data-testid="screen-heading"
              style={{ letterSpacing: "-0.01em", margin: 0 }}
            >
              {heading}
            </Title>
          </Group>
          <Text size="sm" c="gray.6" mt={6} data-testid="screen-subheading">
            What would you like to do first?
          </Text>
        </Box>

        <Stack gap="sm" component="nav" aria-label="Get started actions">
          {GET_STARTED_ACTIONS.map((action) => {
            const { Icon } = action;
            const href = resolveProUrl(action.path);
            return (
              <Button
                key={action.key}
                component="a"
                href={href}
                fullWidth
                variant={action.recommended ? "filled" : "default"}
                h={CONTROL_HEIGHT}
                px="lg"
                radius="md"
                data-testid={`get-started-${action.key}`}
                onClick={() => onChoose?.({ ...action, href })}
                leftSection={<Icon />}
                rightSection={<ChevronRightIcon />}
                styles={{
                  inner: { justifyContent: "flex-start" },
                  label: { flex: 1, justifyContent: "flex-start" },
                  section: { color: "inherit" },
                }}
              >
                <Group gap="xs" wrap="nowrap" align="center">
                  <Text span fw={500} fz="15px" inherit>
                    {action.label}
                  </Text>
                  {action.recommended ? (
                    <Badge
                      variant="white"
                      color="dark"
                      size="sm"
                      radius="sm"
                      data-testid="recommended-badge"
                    >
                      Recommended
                    </Badge>
                  ) : null}
                </Group>
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </OnboardingStage>
  );
}
